<template>
  <div class="page-container">
    <!-- 筛选区域 -->
    <Filter :params="params" @search="searchData">
      <template #handle>
        <el-form-item>
          <el-button icon="el-icon-plus" type="danger" @click="handleAdd">
            {{ $t('smartRouting.atomicSkill.addSkill') }}
          </el-button>
        </el-form-item>
      </template>
    </Filter>

    <!-- 表格区域 -->
    <Table
      ref="tableRef"
      :tableData="tableDataList"
      :total="data.total"
      @update="updatePage"
      @sort-change="handleSortChange"
    >
      <el-table-column prop="code" :label="$t('smartRouting.atomicSkill.code')" />

      <el-table-column prop="name" :label="$t('smartRouting.atomicSkill.name')" />

      <el-table-column prop="model" :label="$t('smartRouting.atomicSkill.model')" />

      <!-- 适配模型 -->
      <el-table-column prop="aiModel" :label="$t('smartRouting.atomicSkill.aiModel')" min-width="180">
        <template #default="{ row }">
          <el-tooltip
            :content="row.aiModel"
            placement="top"
            :disabled="!row.aiModel"
          >
            <span class="ellipsis-text">{{ row.aiModel || '-' }}</span>
          </el-tooltip>
        </template>
      </el-table-column>

      <!-- 适用领域 -->
      <el-table-column prop="area" :label="$t('smartRouting.atomicSkill.area')" width="250">
        <template #default="{ row }">
          <template v-if="splitStr(row.area).length">
            <el-tag
              v-for="(tag, index) in splitStr(row.area)"
              :key="index + '-' + tag"
              size="small"
              type="primary"
              effect="light"
              style="margin: 2px;"
            >
              {{ tag }}
            </el-tag>
          </template>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="createBy" :label="$t('smartRouting.atomicSkill.createBy')" width="100"/>
      <el-table-column prop="createTime" :label="$t('smartRouting.atomicSkill.createTime')" />
      <el-table-column prop="updateBy" :label="$t('smartRouting.atomicSkill.updateBy')" width="100"/>
      <el-table-column prop="updateTime" :label="$t('smartRouting.atomicSkill.updateTime')" />

      <!-- 操作 -->
      <el-table-column :label="$t('smartRouting.atomicSkill.action')" fixed="right" width="130">
        <template #default="{ row }">
          <el-tooltip :content="$t('smartRouting.atomicSkill.view')" placement="top">
            <el-button type="text" size="small" icon="el-icon-view" @click="handleView(row)" />
          </el-tooltip>
          <el-tooltip :content="$t('smartRouting.atomicSkill.edit')" placement="top">
            <el-button type="text" size="small" icon="el-icon-edit" @click="handleEdit(row)" />
          </el-tooltip>
          <el-tooltip :content="$t('smartRouting.atomicSkill.deliver')" placement="top">
            <el-button type="text" size="small" icon="el-icon-position" @click="openDeliverDialog(row)" />
          </el-tooltip>
          <el-tooltip :content="$t('smartRouting.atomicSkill.delete')" placement="top">
            <el-button type="text" style="color: red;" size="small" icon="el-icon-delete" @click="handleDelete(row)" />
          </el-tooltip>
        </template>
      </el-table-column>
    </Table>

    <!-- 新增/编辑/查看弹窗 -->
    <el-dialog
      :title="dialogTitle"
      v-model="data.dialogVisible"
      destroy-on-close
      :modal="true"
      :append-to-body="true"
      width="660px"
      class="custom-dialog"
    >
      <el-form
        :model="formData"
        label-width="130px"
        ref="formRef"
        class="atomic-form"
      >
        <!-- 名称 -->
        <el-form-item :label="$t('smartRouting.atomicSkill.name')" :required="dialogMode !== 'view'">
          <el-input
            v-model="formData.name"
            :placeholder="$t('smartRouting.atomicSkill.nameInputPlaceholder')"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          />
        </el-form-item>

        <!-- 原子技能文件 -->
        <el-form-item :label="$t('smartRouting.atomicSkill.skillFile')" :required="dialogMode !== 'view'" prop="filePath">
          <template v-if="dialogMode === 'view'">
            <el-input
              :model-value="formData.fileName || '-'"
              disabled
              class="view-input"
            />
          </template>

          <template v-else>
            <div class="upload-box" @click="triggerUpload">
              <div class="upload-info">
                <div v-if="formData.fileName" class="file-name">
                  <span>{{ formData.fileName }}</span>
                </div>
                <div v-else class="upload-placeholder">
                  {{ $t('smartRouting.atomicSkill.fileUploadPlaceholder') }}
                </div>
                <div class="upload-tip">{{ $t('smartRouting.atomicSkill.fileUploadTip') }}</div>
              </div>
              <el-button
                size="small"
                type="primary"
                plain
                @click.stop="triggerUpload"
              >
                {{ formData.filePath ? $t('smartRouting.atomicSkill.reupload') : $t('smartRouting.atomicSkill.chooseFile') }}
              </el-button>
            </div>
            <input
              ref="fileInputRef"
              type="file"
              accept=".md"
              style="display: none"
              @change="handleFileChange"
            />
          </template>
        </el-form-item>

        <!-- 适用领域 -->
        <el-form-item :label="$t('smartRouting.atomicSkill.area')" :required="dialogMode !== 'view'">
          <template v-if="dialogMode === 'view'">
            <template v-if="splitStr(formData.area).length">
              <el-tag
                v-for="(tag, index) in splitStr(formData.area)"
                :key="index + '-' + tag"
                size="small"
                type="primary"
                effect="light"
                class="view-tag"
              >
                {{ tag }}
              </el-tag>
            </template>
            <span v-else>-</span>
          </template>
          <div v-else style="width: 100%;">
            <el-input
              v-model="formData.area"
              :placeholder="$t('smartRouting.atomicSkill.areaInputPlaceholder')"
            />
            <div class="form-tip">{{ $t('smartRouting.atomicSkill.areaInputTip') }}</div>
          </div>
        </el-form-item>

        <!-- 适用对象 -->
        <el-form-item :label="$t('smartRouting.atomicSkill.target')">
          <el-input
            v-model="formData.target"
            :placeholder="$t('smartRouting.atomicSkill.targetPlaceholder')"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          />
        </el-form-item>

        <!-- 适配机型 -->
        <el-form-item :label="$t('smartRouting.atomicSkill.model')" :required="dialogMode !== 'view'">
          <el-input
            v-model="formData.model"
            :placeholder="$t('smartRouting.atomicSkill.modelPlaceholder')"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          />
        </el-form-item>

        <!-- 适配模型 -->
        <el-form-item :label="$t('smartRouting.atomicSkill.aiModel')" :required="dialogMode !== 'view'">
          <el-input
            v-model="formData.aiModel"
            :placeholder="$t('smartRouting.atomicSkill.aiModelPlaceholder')"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          />
        </el-form-item>

        <!-- 适配场景 -->
        <el-form-item :label="$t('smartRouting.atomicSkill.scene')">
          <el-input
            v-model="formData.scene"
            :placeholder="$t('smartRouting.atomicSkill.scenePlaceholder')"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          />
        </el-form-item>

        <!-- 描述 -->
        <el-form-item :label="$t('smartRouting.atomicSkill.description')">
          <el-input
            v-model="formData.description"
            type="textarea"
            :rows="3"
            :placeholder="$t('smartRouting.atomicSkill.descriptionPlaceholder')"
            :disabled="dialogMode === 'view'"
            :class="{ 'view-input': dialogMode === 'view' }"
          />
        </el-form-item>

        <template v-if="dialogMode === 'view'">
          <el-form-item :label="$t('smartRouting.atomicSkill.createBy')">
            <el-input :model-value="formData.createBy" disabled class="view-input" />
          </el-form-item>
          <el-form-item :label="$t('smartRouting.atomicSkill.createTime')">
            <el-input :model-value="formData.createTime" disabled class="view-input" />
          </el-form-item>
          <el-form-item :label="$t('smartRouting.atomicSkill.updateBy')">
            <el-input :model-value="formData.updateBy" disabled class="view-input" />
          </el-form-item>
          <el-form-item :label="$t('smartRouting.atomicSkill.updateTime')">
            <el-input :model-value="formData.updateTime" disabled class="view-input" />
          </el-form-item>
        </template>
      </el-form>

      <template #footer>
        <template v-if="dialogMode === 'view'">
          <el-button size="mini" @click="data.dialogVisible = false">{{ $t('smartRouting.atomicSkill.close') }}</el-button>
        </template>
        <template v-else>
          <el-button size="mini" @click="data.dialogVisible = false">{{ $t('smartRouting.atomicSkill.cancel') }}</el-button>
          <el-button type="primary" size="mini" :loading="submitting" @click="handleSubmit">
            {{ dialogMode === 'edit' ? $t('smartRouting.atomicSkill.save') : $t('smartRouting.atomicSkill.confirm') }}
          </el-button>
        </template>
      </template>
    </el-dialog>

    <!-- 下发组件 -->
    <DeliverDialog
      v-model="data.dialogDeliverVisible"
      :deliverInfo="deliverRow"
      @success="handleDeliverSuccess"
      :title="$t('smartRouting.atomicSkill.deliver')"
      resourceType="SKILL"
    />
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import Table from "@/components/table/index";
import Filter from "@/components/table/filter";
import DeliverDialog from "./components/dialogDeliver.vue";
import { ref, reactive } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import { ossUpload } from "@/utils/oss";

import {
  reqSkillPage,
  reqSkillCreate,
  reqSkillUpdate,
  reqSkillDelete,
} from "@/api/smartRouting/skill";

export default {
  name: "AtomicSkillLibrary",
  components: {
    Table,
    Filter,
    DeliverDialog,
  },
  setup() {
    const { t } = useI18n();

    const state = reactive({
      tableRef: null,
      sortData: {},
    });

    const params = [
      {
        key: "name",
        value: "",
        label: t('smartRouting.atomicSkill.name'),
        placeholder: t('smartRouting.atomicSkill.namePlaceholder'),
      },
      {
        key: "area",
        value: "",
        label: t('smartRouting.atomicSkill.area'),
        placeholder: t('smartRouting.atomicSkill.areaPlaceholder'),
      },
    ];

    const data = reactive({
      page: 1,
      size: 10,
      total: 0,
      dialogVisible: false,
      dialogDeliverVisible: false,
    });

    const tableDataList = ref([]);
    const formRef = ref(null);
    const fileInputRef = ref(null);
    const dialogMode = ref("add");
    const dialogTitle = ref("");
    const editId = ref(null);
    const editCode = ref(null);
    const deliverRow = ref({});
    const query = reactive({});
    const submitting = ref(false);

    const formData = reactive({
      name: "",
      area: "",
      target: "",
      model: "",
      aiModel: "",
      scene: "",
      description: "",
      fileName: "",
      filePath: "",
      fileUrl: "",
      createBy: "",
      createTime: "",
      updateBy: "",
      updateTime: "",
    });

    // ===== 分隔字符串 → 数组（兼容数组 / 逗号 / 顿号） =====
    const splitStr = (val) => {
      if (!val) return [];
      if (Array.isArray(val)) return val.filter(Boolean);
      return String(val)
        .split(/[,，、]/)
        .map((s) => s.trim())
        .filter(Boolean);
    };

    // ===== 列表 =====
    const getData = async () => {
      try {
        const res = await reqSkillPage({
          ...query,
          ...state.sortData,
          page: data.page,
          size: data.size,
        });
        tableDataList.value =
          res?.records || res?.list || res?.data?.records || [];
        data.total = res?.total ?? res?.data?.total ?? 0;
      } catch (error) {
        console.error("getSkillListFailed:", error);
        tableDataList.value = [];
        data.total = 0;
        ElMessage.error(t('smartRouting.atomicSkill.fetchDataFailed'));
      }
    };

    const searchData = (searchParams) => {
      Object.assign(query, searchParams);
      data.page = 1;
      getData();
    };

    const updatePage = (page, size) => {
      data.page = page || 1;
      data.size = size || 10;
      getData();
    };

    // 兼容 { prop, order } 与 (prop, order) 两种回调
    const handleSortChange = (payload, orderMaybe) => {
      let prop, order;
      if (payload && typeof payload === "object") {
        prop = payload.prop;
        order = payload.order;
      } else {
        prop = payload;
        order = orderMaybe;
      }
      const orderMap = { ascending: "asc", descending: "desc" };
      state.sortData = {
        sortField: prop || "",
        sort: orderMap[order] || "",
      };
      data.page = 1;
      getData();
    };

    // ===== 重置表单（字段与 formData 对齐） =====
    const resetForm = () => {
      Object.assign(formData, {
        name: "",
        area: "",
        target: "",
        model: "",
        aiModel: "",
        scene: "",
        description: "",
        fileName: "",
        filePath: "",
        fileUrl: "",
        createBy: "",
        createTime: "",
        updateBy: "",
        updateTime: "",
      });
      editCode.value = null;
      if (fileInputRef.value) {
        fileInputRef.value.value = "";
      }
    };

    const handleAdd = () => {
      dialogMode.value = "add";
      dialogTitle.value = t('smartRouting.atomicSkill.addSkill');
      editId.value = null;
      resetForm();
      data.dialogVisible = true;
    };

    const handleEdit = (row) => {
      dialogMode.value = "edit";
      dialogTitle.value = t('smartRouting.atomicSkill.editSkill');
      editId.value = row.id;
      editCode.value = row.code;
      Object.assign(formData, {
        name: row.name || "",
        area: Array.isArray(row.area) ? row.area.join(",") : row.area || "",
        target: row.target || "",
        model: Array.isArray(row.model) ? row.model.join(",") : row.model || "",
        aiModel: row.aiModel || "",
        scene: row.scene || "",
        description: row.description || "",
        fileName: row.fileName || "",
        filePath: row.filePath || "",
        fileUrl: row.fileUrl || "",
      });
      data.dialogVisible = true;
    };

    const handleView = (row) => {
      dialogMode.value = "view";
      dialogTitle.value = t('smartRouting.atomicSkill.viewSkill');
      editId.value = row.id;
      editCode.value = row.code;
      Object.assign(formData, {
        name: row.name || "",
        area: row.area || "",
        target: row.target || "",
        model: row.model || "",
        aiModel: row.aiModel || "",
        scene: row.scene || "",
        description: row.description || "",
        fileName: row.fileName || "",
        filePath: row.filePath || "",
        fileUrl: row.fileUrl || "",
        createBy: row.createBy || "",
        createTime: row.createTime || "",
        updateBy: row.updateBy || "",
        updateTime: row.updateTime || "",
      });
      data.dialogVisible = true;
    };

    const triggerUpload = () => {
      fileInputRef.value?.click();
    };

    const handleFileChange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!/\.md$/i.test(file.name)) {
        ElMessage.warning(t('smartRouting.atomicSkill.fileFormatTip'));
        e.target.value = "";
        return;
      }

      formData.fileName = file.name;
      formData.filePath =
        "smart/atomic/" + dayjs().format("YYYY/MM/DD/") + file.name;

      try {
        formData.fileUrl = await ossUpload(file.name, file, {
          path: "smart/atomic/" + dayjs().format("YYYY/MM/DD"),
          timestamp: false,
        });
        ElMessage.success(t('smartRouting.atomicSkill.fileUploadSuccess'));
      } catch (error) {
        console.error("uploadFailed:", error);
        ElMessage.error(t('smartRouting.atomicSkill.fileUploadFailed'));
        formData.fileName = "";
        formData.filePath = "";
        formData.fileUrl = "";
      } finally {
        e.target.value = "";
      }
    };

    const handleSubmit = async () => {
      if (!formData.name) {
        ElMessage.warning(t('smartRouting.atomicSkill.nameTip'));
        return;
      }
      if (!formData.fileName) {
        ElMessage.warning(t('smartRouting.atomicSkill.fileTip'));
        return;
      }
      if (!formData.model) {
        ElMessage.warning(t('smartRouting.atomicSkill.modelTip'));
        return;
      }
      if (!formData.aiModel) {
        ElMessage.warning(t('smartRouting.atomicSkill.aiModelTip'));
        return;
      }

      // area 统一转成逗号字符串提交
      const area = Array.isArray(formData.area)
        ? formData.area.join(",")
        : formData.area || "";

      const payload = {
        name: formData.name,
        area,
        target: formData.target || "",
        model: formData.model || "",
        aiModel: formData.aiModel || "",
        scene: formData.scene || "",
        description: formData.description || "",
        fileName: formData.fileName,
        filePath: formData.filePath,
        fileUrl: formData.fileUrl,
      };

      submitting.value = true;
      try {
        if (dialogMode.value === "edit") {
          await reqSkillUpdate({
            id: editId.value,
            code: editCode.value,
            ...payload,
          });
          ElMessage.success(t('smartRouting.atomicSkill.editSuccess'));
        } else {
          await reqSkillCreate(payload);
          ElMessage.success(t('smartRouting.atomicSkill.addSuccess'));
        }
        data.dialogVisible = false;
        getData();
      } catch (error) {
        console.error("submitFailed:", error);
        ElMessage.error(dialogMode.value === "edit" ? t('smartRouting.atomicSkill.editFailed') : t('smartRouting.atomicSkill.addFailed'));
      } finally {
        submitting.value = false;
      }
    };

    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          `${t('smartRouting.atomicSkill.confirmDelete')} "${row.name}" ?`,
          t('smartRouting.atomicSkill.deleteConfirmTitle'),
          {
            confirmButtonText: t('smartRouting.atomicSkill.confirm'),
            cancelButtonText: t('smartRouting.atomicSkill.cancel'),
            type: "warning",
          }
        );
        await reqSkillDelete(row.code);
        ElMessage.success(t('smartRouting.atomicSkill.deleteSuccess'));
        if (tableDataList.value.length === 1 && data.page > 1) {
          data.page -= 1;
        }
        getData();
      } catch (error) {
        if (error !== "cancel") {
          console.error("deleteFailed:", error);
          ElMessage.error(t('smartRouting.atomicSkill.deleteFailed'));
        }
      }
    };

    const openDeliverDialog = (row) => {
      deliverRow.value = {
        id: row.id,
        name: row.name,
        fileName: row.fileName,
        filePath: row.filePath,
        fileUrl: row.fileUrl,
        model: row.model,
        aiModel: row.aiModel,
        area: row.area,
        code: row.code,
      };
      data.dialogDeliverVisible = true;
    };

    const handleDeliverSuccess = ({ deviceIds } = {}) => {
      const count = deviceIds?.length ?? 0;
      // 多语言拼接处理
      ElMessage.success(
        `"${deliverRow.value.name}" ${t('smartRouting.atomicSkill.deliverSuccess')} ${count} ${t('smartRouting.atomicSkill.deliverSuccessDevice')}`
      );
    };

    getData();

    return {
      tableRef: state.tableRef,
      params,
      data,
      tableDataList,
      formRef,
      fileInputRef,
      dialogMode,
      dialogTitle,
      formData,
      editId,
      editCode,
      deliverRow,
      submitting,
      splitStr,
      searchData,
      updatePage,
      handleSortChange,
      handleAdd,
      handleEdit,
      handleView,
      handleSubmit,
      handleDelete,
      triggerUpload,
      handleFileChange,
      openDeliverDialog,
      handleDeliverSuccess,
    };
  },
};
</script>

<style lang="scss" scoped>
/* 样式保持不变 */
.page-container {
  height: 100%;
  padding: 50px 10px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.ellipsis-text {
  display: inline-block;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.atomic-form {
  padding-right: 10px;
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin-top: 4px;
}

.upload-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  background-color: #fafafa;
  cursor: pointer;
  width: 100%;
  box-sizing: border-box;
}

.upload-box:hover {
  border-color: #409eff;
}

.upload-info {
  flex: 1;
}

.upload-placeholder {
  color: #c0c4cc;
}

.file-name {
  display: flex;
  align-items: center;
  color: #67c23a;
  margin-bottom: 6px;
}

.upload-tip {
  color: #909399;
  font-size: 12px;
}

.view-tag {
  margin-right: 8px;
  margin-bottom: 4px;
}

.view-input :deep(.el-input__wrapper),
.view-input :deep(.el-textarea__inner) {
  background-color: #f5f7fa;
  box-shadow: 0 0 0 1px #e4e7ed inset;
  color: #606266;
  -webkit-text-fill-color: #606266;
}

.view-input :deep(.el-textarea__inner) {
  resize: none;
}
</style>